import { getOverlapMapFromSlots } from "@gymflow/helpers";
import { AvailabilityDTO, AvailabilitySlot, DayOfWeek } from "@gymflow/types";
import { TIME_FORMAT } from "@gymflow/validation";
import { createColumnHelper } from "@tanstack/react-table";
import classNames from "classnames";
import { capitalize } from "lodash";
import cloneDeep from "lodash/cloneDeep";
import moment from "moment-timezone";
import { useEffect, useState } from "react";
import { Mutable } from "utility-types";

import {
  Button,
  LabeledSwitch,
  PlusCircleIcon,
  Switch,
  TrashIcon,
} from "../../atoms";
import { Table } from "..";
import { TimeInterval } from "./TimeInterval";

type AvailabilityTableProps = {
  availabilityType: "STAFF" | "FACILITY";
  value: AvailabilityDTO | null;
  isLoading: boolean;
  isDisabled: boolean;
  onChange: (slots: AvailabilitySlot[]) => Promise<void>;
};

// Need to refactor it (together with BE)
export function AvailabilityTable({
  availabilityType,
  value,
  onChange,
  isLoading = false,
  isDisabled = false,
}: AvailabilityTableProps) {
  const [editing, setEditing] = useState<AvailabilityDTO | null>(null);

  useEffect(() => {
    if (!value) {
      return;
    }
    return setEditing({ ...value });
  }, [value]);

  const weekdays: DayOfWeek[] = [
    "MONDAY",
    "TUESDAY",
    "WEDNESDAY",
    "THURSDAY",
    "FRIDAY",
    "SATURDAY",
    "SUNDAY",
  ];
  const slots = weekdays.map((weekday) => {
    return {
      weekday,
      slots: editing
        ? editing.availabilitySlotList.reduce((acc, slot, idx) => {
            if (slot.weekDay === weekday) {
              acc.push({
                ...slot,
                editingIdx: idx,
              });
            }
            return acc;
          }, [] as AvailabilitySlotColumn[])
        : [],
    };
  });

  // Refactor this logic after refactoring on BE side.
  // More in info in the comment from BE:
  // https://linear.app/gymflow/issue/GYM-1131/[be]-improve-availability-on-staff-portalstaff-app#comment-f0ae42fa
  // and
  // https://linear.app/gymflow/issue/GYM-2233/allow-selecting-end-of-day-for-availability
  const overlapMap = getOverlapMapFromSlots(
    editing?.availabilitySlotList ?? [],
  );

  const columnHelper = createColumnHelper<{
    weekday: DayOfWeek;
    slots: AvailabilitySlotColumn[];
  }>();
  const columns = [
    columnHelper.accessor("weekday", {
      header: "Day",
      cell: (column) => {
        return capitalize(column.row.original.weekday);
      },
    }),
    columnHelper.display({
      header: "Available?",
      cell: (column) => {
        const weekday = column.row.original.weekday;
        const isWeekdayEnabled = column.row.original.slots.length > 0;

        return (
          <Switch
            value={isWeekdayEnabled}
            disabled={isDisabled}
            onChange={(checked) => {
              if (checked) {
                const defaultSlotValue: AvailabilitySlot = {
                  startTime: "09:00:00",
                  endTime: "17:00:00",
                  availabilityType: availabilityType,
                  weekDay: weekday,
                };
                setEditing((prev) => {
                  const newValue = cloneDeep(prev);
                  newValue!.availabilitySlotList.push(defaultSlotValue);
                  return newValue;
                });
              } else {
                setEditing((prev) => {
                  const newValue: Mutable<AvailabilityDTO | null> =
                    cloneDeep(prev);
                  newValue!.availabilitySlotList =
                    prev!.availabilitySlotList.filter(
                      (slot) => slot.weekDay !== weekday,
                    );
                  return newValue;
                });
              }
            }}
          />
        );
      },
    }),
    columnHelper.accessor("slots", {
      header: "Available Shifts",
      cell: (column) => {
        if (column.row.original.slots.length === 0) {
          return (
            <div className="flex min-h-11 min-w-[33rem] items-center text-gray-600">
              Not Available
            </div>
          );
        }

        // Refactor this logic after refactoring on BE side.
        // More in info in the comment from BE:
        // https://linear.app/gymflow/issue/GYM-1131/[be]-improve-availability-on-staff-portalstaff-app#comment-f0ae42fa
        const isAvailableAllDay = column.row.original.slots.some(
          (slot) => slot.startTime === "00:00:00" && slot.endTime === null,
        );

        return (
          <div className="flex min-w-[33rem] flex-row py-2">
            {!isAvailableAllDay && (
              <div className="flex flex-col gap-y-2">
                {column.row.original.slots.map((slot, idx) => {
                  const isLastRow =
                    idx + 1 === column.row.original.slots.length;
                  return (
                    <div
                      className="flex items-center gap-1"
                      key={`${slot.startTime}-${slot.endTime}`}
                    >
                      <TimeInterval
                        key={slot.startTime}
                        startTime={moment(slot.startTime, TIME_FORMAT).format(
                          "HH:mm",
                        )}
                        endTime={
                          slot.endTime === null
                            ? null
                            : moment(slot.endTime, TIME_FORMAT).format("HH:mm")
                        }
                        onChange={(editedValue) => {
                          setEditing((prev) => {
                            const newEditing = cloneDeep(prev)!;
                            newEditing.availabilitySlotList[slot.editingIdx] = {
                              ...newEditing.availabilitySlotList[
                                slot.editingIdx
                              ],
                              startTime: editedValue.startTime + ":00",
                              endTime:
                                editedValue.endTime === null
                                  ? null
                                  : editedValue.endTime + ":00",
                            };
                            return newEditing;
                          });
                        }}
                      />
                      <div
                        className={classNames("ml-2 cursor-pointer", {
                          hidden: !isLastRow || slot.endTime === null,
                        })}
                        onClick={() => {
                          const endMoment = moment(slot.endTime, "HH:mm");
                          setEditing((prev) => {
                            return {
                              availabilitySlotList: [
                                ...prev!.availabilitySlotList,
                                {
                                  weekDay: column.row.original.weekday,
                                  startTime: endMoment
                                    .clone()
                                    .add(0, "minutes")
                                    .format("HH:mm:ss"),
                                  endTime:
                                    slot.endTime === "23:55:00"
                                      ? null
                                      : endMoment
                                          .clone()
                                          .add(5, "minutes")
                                          .format("HH:mm:ss"),
                                  availabilityType: availabilityType,
                                },
                              ],
                            };
                          });
                        }}
                      >
                        <PlusCircleIcon
                          className="mr-2 self-center"
                          pathClassName="stroke-primary"
                        />
                      </div>
                      <div
                        className="cursor-pointer"
                        onClick={() => {
                          setEditing((prev) => {
                            const newSlots = prev!.availabilitySlotList.filter(
                              (s) =>
                                s.weekDay !== slot.weekDay ||
                                s.startTime !== slot.startTime ||
                                s.endTime !== slot.endTime,
                            );
                            return {
                              availabilitySlotList: newSlots,
                            };
                          });
                        }}
                      >
                        <TrashIcon
                          className="mr-2 self-center"
                          pathClassName="stroke-primary"
                        />
                      </div>
                    </div>
                  );
                })}
                {overlapMap[column.row.original.weekday] && (
                  <div className="text-error-700 my-2">
                    Some intervals overlap.
                  </div>
                )}
              </div>
            )}
            <LabeledSwitch
              value={isAvailableAllDay}
              disabled={isDisabled}
              label="Available All Day"
              onChange={(checked) => {
                // Refactor this logic after refactoring on BE side.
                setEditing((prev) => {
                  if (!prev) return prev;

                  const weekday = column.row.original.weekday;
                  const newEditing: Mutable<AvailabilityDTO | null> =
                    cloneDeep(prev);

                  // More in info in the comment from BE:
                  // https://linear.app/gymflow/issue/GYM-1131/[be]-improve-availability-on-staff-portalstaff-app#comment-f0ae42fa
                  if (checked) {
                    newEditing.availabilitySlotList =
                      newEditing.availabilitySlotList
                        .filter((slot) => slot.weekDay !== weekday)
                        .concat([
                          {
                            weekDay: weekday,
                            startTime: "00:00:00",
                            endTime: null,
                            availabilityType,
                          },
                        ]);
                  } else {
                    newEditing.availabilitySlotList =
                      newEditing.availabilitySlotList.map((slot) =>
                        slot.weekDay === weekday &&
                        slot.startTime === "00:00:00" &&
                        slot.endTime === null
                          ? { ...slot, endTime: "14:00:00" }
                          : slot,
                      );
                  }

                  return newEditing;
                });
              }}
            />
          </div>
        );
      },
    }),
  ];

  return (
    <>
      {value && (
        <Table
          rowClassName="h-14"
          data={slots}
          columns={columns}
          isFetching={isLoading}
          pageCount={1}
          pageSize={10}
        />
      )}
      <div className="m-4 flex flex-row-reverse">
        <Button
          intent="primary"
          className="mt-2 w-32"
          disabled={Object.values(overlapMap).some((v) => v)}
          onClick={async () => {
            if (!value) {
              return;
            }
            await onChange(editing!.availabilitySlotList);
          }}
        >
          Save
        </Button>
      </div>
    </>
  );
}

interface AvailabilitySlotColumn extends AvailabilitySlot {
  editingIdx: number;
}
